import { apiSlice } from "./apiSlice";
import { RBAC_URL } from "../constants";

export const rbacApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── บทบาท = ชุดสิทธิ์ (migration 129 · 20 ต.ค. 69) — back/controllers/roleController.js ──
    getRoleAdmin: builder.query({
      query: () => ({ url: `${RBAC_URL}/role-admin` }),
      providesTags: ["RoleAdmin"],
    }),
    createRole: builder.mutation({
      query: (body) => ({ url: `${RBAC_URL}/roles`, method: "POST", body }),
      invalidatesTags: ["RoleAdmin"],
    }),
    updateRole: builder.mutation({
      query: ({ id, ...body }) => ({ url: `${RBAC_URL}/roles/${id}`, method: "PUT", body }),
      invalidatesTags: ["RoleAdmin", "Org"],
    }),
    deleteRole: builder.mutation({
      query: (id) => ({ url: `${RBAC_URL}/roles/${id}`, method: "DELETE" }),
      invalidatesTags: ["RoleAdmin"],
    }),
    setUserRoleSet: builder.mutation({
      query: ({ userId, role_ids }) => ({ url: `${RBAC_URL}/users/${userId}/role-set`, method: "PUT", body: { role_ids } }),
      invalidatesTags: ["RoleAdmin", "Org"],
    }),
    setRbacSettings: builder.mutation({
      query: (body) => ({ url: `${RBAC_URL}/settings`, method: "PUT", body }),
      invalidatesTags: ["RoleAdmin"],
    }),
    getAccessDenials: builder.query({
      query: (days = 14) => ({ url: `${RBAC_URL}/access-denials?days=${days}` }),
      providesTags: ["RoleAdmin"],
    }),
    getMatrix: builder.query({
      query: () => ({ url: `${RBAC_URL}/matrix` }),
      providesTags: ["RbacMatrix"],
    }),
    updateMatrix: builder.mutation({
      query: (data) => ({
        url: `${RBAC_URL}/matrix`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["RbacMatrix"],
    }),
    // เมทริกซ์ แผนก x สิทธิ์ — ใช้ข้อมูลก้อนเดียวกับ getMatrix
    // จึงไม่ต้องมี query แยก มีแต่ mutation สำหรับบันทึก
    updateDeptMatrix: builder.mutation({
      query: (data) => ({
        url: `${RBAC_URL}/dept-matrix`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["RbacMatrix"],
    }),
    getPermissionHolders: builder.query({
      query: (key) => ({ url: `${RBAC_URL}/permissions/${key}/holders` }),
    }),
    getRoles: builder.query({
      query: () => ({ url: `${RBAC_URL}/roles` }),
      providesTags: ["RbacRoles"],
    }),
    getDepartmentsRbac: builder.query({
      query: () => ({ url: `${RBAC_URL}/departments` }),
      providesTags: ["RbacDepts"],
    }),
    getUserAccess: builder.query({
      query: (userId) => ({ url: `${RBAC_URL}/users/${userId}/access` }),
      providesTags: (result, error, userId) => [{ type: "UserAccess", id: userId }],
    }),
    updateUserRoles: builder.mutation({
      query: ({ userId, roleIds, primaryRoleId }) => ({
        url: `${RBAC_URL}/users/${userId}/roles`,
        method: "PUT",
        body: { roleIds, primaryRoleId },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "UserAccess", id: userId },
        "User",
      ],
    }),
    updateUserDepartments: builder.mutation({
      query: ({ userId, departments }) => ({
        url: `${RBAC_URL}/users/${userId}/departments`,
        method: "PUT",
        body: { departments },
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: "UserAccess", id: userId },
        "Org",
      ],
    }),
    // การเข้าสู่ระบบของคน: แก้อีเมล · ยกเลิกเชื่อม Google · เปิด/ปิดเข้าด้วยรหัสผ่าน
    updateLoginSettings: builder.mutation({
      query: ({ userId, ...body }) => ({
        url: `${RBAC_URL}/users/${userId}/login`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    // คำขอเข้าใช้ — คนที่กดเข้าด้วย Google แต่ยังไม่มีบัญชี
    getAccessRequests: builder.query({
      query: () => ({ url: `${RBAC_URL}/access-requests` }),
      providesTags: ["User"],
    }),
    approveAccessRequest: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `${RBAC_URL}/access-requests/${id}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["User"],
    }),
    rejectAccessRequest: builder.mutation({
      query: (id) => ({
        url: `${RBAC_URL}/access-requests/${id}/reject`,
        method: "POST",
      }),
      invalidatesTags: ["User"],
    }),
    // ตั้ง/ถอดผู้ดูแลระบบ — หลังบ้านกันถอดตัวเองและถอดจนไม่เหลือแอดมิน + บันทึกทุกครั้ง
    setUserAdmin: builder.mutation({
      query: ({ userId, isAdmin }) => ({
        url: `${RBAC_URL}/users/${userId}/admin`,
        method: "PUT",
        body: { isAdmin },
      }),
      invalidatesTags: (result, error, { userId }) => [{ type: "UserAccess", id: userId }, "User"],
    }),
    getMyPermissions: builder.query({
      query: () => ({ url: `${RBAC_URL}/me/permissions` }),
      providesTags: ["User"],
    }),
  }),
});

export const {
  useGetMatrixQuery,
  useUpdateMatrixMutation,
  useUpdateDeptMatrixMutation,
  useGetPermissionHoldersQuery,
  useLazyGetPermissionHoldersQuery,
  useGetRolesQuery,
  useGetDepartmentsRbacQuery,
  useGetUserAccessQuery,
  useUpdateUserRolesMutation,
  useUpdateUserDepartmentsMutation,
  useSetUserAdminMutation,
  useUpdateLoginSettingsMutation,
  useGetAccessRequestsQuery,
  useApproveAccessRequestMutation,
  useRejectAccessRequestMutation,
  useGetMyPermissionsQuery,
  useGetRoleAdminQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useSetUserRoleSetMutation,
  useSetRbacSettingsMutation,
  useGetAccessDenialsQuery,
} = rbacApiSlice;
